import { hasPermission } from "@edm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Search, Upload } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CsvImportDialog, type ImportDataType } from "@/components/csv-import-dialog";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError } from "@/lib/api";
import { useMe } from "@/lib/auth";
import {
  type CompanyRow,
  createResource,
  type DepartmentRow,
  type LocationRow,
  listResource,
  type PersonnelRow,
  updateResource,
  useReferenceData,
  type WarehouseRow,
  warehouseTypeLabel,
} from "@/lib/master-data";
import { cn } from "@/lib/utils";

type TabKey = "companies" | "departments" | "locations" | "warehouses" | "personnel";
type Row = CompanyRow | DepartmentRow | LocationRow | WarehouseRow | PersonnelRow;
type FormValue = string | boolean;

const TABS: { key: TabKey; label: string; endpoint: string; createLabel: string }[] = [
  { key: "companies", label: "公司", endpoint: "/api/v1/admin/companies", createLabel: "新增公司" },
  {
    key: "departments",
    label: "部门",
    endpoint: "/api/v1/admin/departments",
    createLabel: "新增部门",
  },
  {
    key: "locations",
    label: "使用位置",
    endpoint: "/api/v1/admin/locations",
    createLabel: "新增位置",
  },
  {
    key: "warehouses",
    label: "仓库",
    endpoint: "/api/v1/admin/warehouses",
    createLabel: "新增仓库",
  },
  { key: "personnel", label: "人员", endpoint: "/api/v1/admin/personnel", createLabel: "新增人员" },
];

const DEFAULT_FORM: Record<TabKey, Record<string, FormValue>> = {
  companies: { code: "", name: "", isActive: true },
  departments: {
    companyId: "",
    parentId: "",
    name: "",
    isCostCenter: false,
    managerUserId: "",
    sortOrder: "0",
    isActive: true,
  },
  locations: {
    companyId: "",
    name: "",
    address: "",
    building: "",
    floor: "",
    room: "",
    isActive: true,
  },
  warehouses: {
    companyId: "",
    warehouseType: "entity",
    locationId: "",
    managerUserId: "",
    name: "",
    isActive: true,
  },
  personnel: {
    employeeNo: "",
    name: "",
    companyId: "",
    departmentId: "",
    managerId: "",
    position: "",
    phone: "",
    email: "",
    adid: "",
    isActive: true,
  },
};

function text(value: FormValue | undefined): string {
  return typeof value === "string" ? value.trim() : "";
}
function nullable(value: FormValue | undefined): string | null {
  const trimmed = text(value);
  return trimmed || null;
}
function bool(value: FormValue | undefined): boolean {
  return value === true;
}
function numberValue(value: FormValue | undefined): number {
  const parsed = Number(text(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildBody(tab: TabKey, form: Record<string, FormValue>, editing: boolean): unknown {
  if (tab === "companies") {
    return {
      ...(!editing && text(form.code) ? { code: text(form.code) } : {}),
      name: text(form.name),
      isActive: bool(form.isActive),
    };
  }
  if (tab === "departments") {
    return {
      companyId: text(form.companyId),
      parentId: nullable(form.parentId),
      ...(!editing && text(form.code) ? { code: text(form.code) } : {}),
      name: text(form.name),
      isCostCenter: bool(form.isCostCenter),
      managerUserId: nullable(form.managerUserId),
      sortOrder: numberValue(form.sortOrder),
      isActive: bool(form.isActive),
    };
  }
  if (tab === "locations") {
    return {
      companyId: nullable(form.companyId),
      name: text(form.name),
      address: nullable(form.address),
      building: nullable(form.building),
      floor: nullable(form.floor),
      room: nullable(form.room),
      isActive: bool(form.isActive),
    };
  }
  if (tab === "warehouses") {
    return {
      companyId: nullable(form.companyId),
      warehouseType: text(form.warehouseType) || "other",
      locationId: nullable(form.locationId),
      managerUserId: nullable(form.managerUserId),
      name: text(form.name),
      isActive: bool(form.isActive),
    };
  }
  return {
    employeeNo: nullable(form.employeeNo),
    name: text(form.name),
    companyId: nullable(form.companyId),
    departmentId: nullable(form.departmentId),
    managerId: nullable(form.managerId),
    position: nullable(form.position),
    phone: nullable(form.phone),
    email: nullable(form.email),
    adid: nullable(form.adid),
    isActive: bool(form.isActive),
  };
}

function StatusBadge({ active }: { active: boolean }) {
  return <Badge variant={active ? "success" : "secondary"}>{active ? "启用" : "停用"}</Badge>;
}

export function SettingsOrganizationPage() {
  const [tab, setTab] = useState<TabKey>("companies");
  const [query, setQuery] = useState("");
  const [matchPersonnel, setMatchPersonnel] = useState<PersonnelRow | null>(null);
  const [matchUserId, setMatchUserId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  const [form, setForm] = useState<Record<string, FormValue>>(DEFAULT_FORM.companies);
  const [submitting, setSubmitting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const reference = useReferenceData();
  const activeTab = TABS.find((item) => item.key === tab) ?? (TABS[0] as (typeof TABS)[number]);
  const importType: ImportDataType | null =
    tab === "companies"
      ? "company"
      : tab === "departments"
        ? "department"
        : tab === "personnel"
          ? "personnel"
          : null;
  const canImport =
    Boolean(me) &&
    ((tab === "personnel" && hasPermission(me?.permissions ?? [], "admin:personnel:manage")) ||
      ((tab === "companies" || tab === "departments") &&
        hasPermission(me?.permissions ?? [], "admin:basic-data:manage")));
  const listQuery = useQuery({
    queryKey: ["org", tab],
    queryFn: () => listResource<Row>(activeTab.endpoint),
  });

  const companies = reference.data?.companies ?? [];
  const departments = reference.data?.departments ?? [];
  const users = reference.data?.users ?? [];
  const locations = reference.data?.locations ?? [];
  const personnel = (listQuery.data ?? []) as PersonnelRow[];

  const companyName = (id: string | null | undefined) =>
    companies.find((row) => row.id === id)?.name ?? "—";
  const departmentName = (id: string | null | undefined) =>
    departments.find((row) => row.id === id)?.name ?? "—";
  const userName = (id: string | null | undefined) =>
    users.find((row) => row.id === id)?.displayName ?? "—";
  const locationName = (id: string | null | undefined) =>
    locations.find((row) => row.id === id)?.name ?? "—";
  const personnelName = (id: string | null | undefined) =>
    personnel.find((row) => row.id === id)?.name ?? "—";

  const filteredRows = useMemo(() => {
    const rows = (listQuery.data ?? []) as Row[];
    if (!query.trim()) return rows;
    const keyword = query.trim().toLowerCase();
    return rows.filter((row) => JSON.stringify(row).toLowerCase().includes(keyword));
  }, [listQuery.data, query]);

  function openCreate() {
    setEditing(null);
    setForm({ ...DEFAULT_FORM[tab] });
    setDialogOpen(true);
  }

  function openEdit(row: Row) {
    setEditing(row);
    const next: Record<string, FormValue> = { ...DEFAULT_FORM[tab], id: row.id };
    for (const [key, value] of Object.entries(row)) {
      if (value === null || value === undefined) continue;
      next[key] = typeof value === "boolean" ? value : String(value);
    }
    setForm(next);
    setDialogOpen(true);
  }

  function setField(key: string, value: FormValue) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function submit() {
    if (!text(form.name)) {
      toast.error("请填写名称");
      return;
    }
    setSubmitting(true);
    const path = editing ? `${activeTab.endpoint}/${editing.id}` : activeTab.endpoint;
    try {
      if (editing) await updateResource(path, buildBody(tab, form, true));
      else await createResource(path, buildBody(tab, form, false));
      toast.success(editing ? "已更新" : "已创建");
      setDialogOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["org", tab] }),
        queryClient.invalidateQueries({ queryKey: ["reference-data"] }),
      ]);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "保存失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  }

  const columns = getColumns(tab, {
    companyName,
    departmentName,
    userName,
    locationName,
    personnelName,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="组织与人员"
        description="维护公司、部门（成本中心）、使用位置、仓库与人员基础数据；人员成本中心由部门属性自动派生。"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => listQuery.refetch()}
              disabled={listQuery.isFetching}
            >
              <RefreshCw className={cn("h-4 w-4", listQuery.isFetching && "animate-spin")} />
              刷新
            </Button>
            {canImport && importType ? (
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <Upload className="h-4 w-4" />
                批量导入
              </Button>
            ) : null}
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {activeTab.createLabel}
            </Button>
          </>
        }
      />

      <Card>
        <CardContent className="space-y-4 p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Tabs
              value={tab}
              onValueChange={(value) => {
                setTab(value as TabKey);
                setQuery("");
              }}
            >
              <TabsList className="flex-wrap">
                {TABS.map((item) => (
                  <TabsTrigger key={item.key} value={item.key}>
                    {item.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜索当前列表"
                className="pl-9"
              />
            </div>
          </div>

          {listQuery.isLoading ? (
            <div className="space-y-2">
              {["s1", "s2", "s3", "s4", "s5", "s6"].map((key) => (
                <Skeleton key={key} className="h-11 w-full" />
              ))}
            </div>
          ) : filteredRows.length === 0 ? (
            <EmptyState
              title="当前列表为空"
              description="点击右上角新增按钮建立第一条基础数据。"
              action={{ label: activeTab.createLabel, to: "/settings/organization" }}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((column) => (
                    <TableHead key={column.key}>{column.label}</TableHead>
                  ))}
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow key={row.id}>
                    {columns.map((column) => (
                      <TableCell key={column.key}>{column.render(row)}</TableCell>
                    ))}
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
                        编辑
                      </Button>
                      {tab === "personnel" ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMatchPersonnel(row as PersonnelRow);
                            setMatchUserId((row as PersonnelRow).userId ?? "");
                          }}
                        >
                          绑定账号
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {importType ? (
        <CsvImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          dataType={importType}
          onImported={() => {
            queryClient.invalidateQueries({ queryKey: ["org", tab] });
            queryClient.invalidateQueries({ queryKey: ["reference-data"] });
          }}
        />
      ) : null}

      <Dialog
        open={Boolean(matchPersonnel)}
        onOpenChange={(open) => !open && setMatchPersonnel(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>绑定账号：{matchPersonnel?.name}</DialogTitle>
            <DialogDescription>一个账号只能绑定一个人员；清除绑定可解除。</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>账号</Label>
            <NativeSelect value={matchUserId} onChange={setMatchUserId}>
              <option value="">不绑定</option>
              {(reference.data?.users ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName}
                </option>
              ))}
            </NativeSelect>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMatchPersonnel(null)}>
              取消
            </Button>
            <Button
              onClick={async () => {
                if (!matchPersonnel) return;
                try {
                  await updateResource(`/api/v1/admin/personnel/${matchPersonnel.id}/match`, {
                    userId: matchUserId || null,
                  });
                  await queryClient.invalidateQueries({ queryKey: ["org", "personnel"] });
                  toast.success("账号绑定已更新");
                  setMatchPersonnel(null);
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : "绑定失败");
                }
              }}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? `编辑${activeTab.label}` : activeTab.createLabel}</DialogTitle>
            <DialogDescription>编码留空时由系统按编号规则自动生成。</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            {renderFields(tab, form, setField, {
              companies,
              departments,
              users,
              locations,
              personnel,
              editing: Boolean(editing),
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? "保存中…" : "保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface NameResolvers {
  companyName: (id: string | null | undefined) => string;
  departmentName: (id: string | null | undefined) => string;
  userName: (id: string | null | undefined) => string;
  locationName: (id: string | null | undefined) => string;
  personnelName: (id: string | null | undefined) => string;
}

function getColumns(tab: TabKey, resolve: NameResolvers) {
  if (tab === "companies") {
    return [
      { key: "code", label: "编码", render: (row: Row) => (row as CompanyRow).code },
      { key: "name", label: "名称", render: (row: Row) => (row as CompanyRow).name },
      {
        key: "status",
        label: "状态",
        render: (row: Row) => <StatusBadge active={(row as CompanyRow).isActive} />,
      },
    ];
  }
  if (tab === "departments") {
    return [
      { key: "code", label: "编码", render: (row: Row) => (row as DepartmentRow).code },
      { key: "name", label: "名称", render: (row: Row) => (row as DepartmentRow).name },
      {
        key: "company",
        label: "公司",
        render: (row: Row) => resolve.companyName((row as DepartmentRow).companyId),
      },
      {
        key: "costCenter",
        label: "成本中心",
        render: (row: Row) =>
          (row as DepartmentRow).isCostCenter ? <Badge variant="warning">是</Badge> : "否",
      },
      {
        key: "manager",
        label: "负责人",
        render: (row: Row) => resolve.userName((row as DepartmentRow).managerUserId),
      },
      {
        key: "status",
        label: "状态",
        render: (row: Row) => <StatusBadge active={(row as DepartmentRow).isActive} />,
      },
    ];
  }
  if (tab === "locations") {
    return [
      { key: "code", label: "编码", render: (row: Row) => (row as LocationRow).code },
      { key: "name", label: "名称", render: (row: Row) => (row as LocationRow).name },
      {
        key: "company",
        label: "公司",
        render: (row: Row) => resolve.companyName((row as LocationRow).companyId),
      },
      {
        key: "address",
        label: "地址",
        render: (row: Row) => (row as LocationRow).address ?? "—",
      },
      {
        key: "status",
        label: "状态",
        render: (row: Row) => <StatusBadge active={(row as LocationRow).isActive} />,
      },
    ];
  }
  if (tab === "warehouses") {
    return [
      { key: "code", label: "编码", render: (row: Row) => (row as WarehouseRow).code },
      { key: "name", label: "名称", render: (row: Row) => (row as WarehouseRow).name },
      {
        key: "company",
        label: "公司",
        render: (row: Row) => resolve.companyName((row as WarehouseRow).companyId),
      },
      {
        key: "type",
        label: "类型",
        render: (row: Row) => warehouseTypeLabel((row as WarehouseRow).warehouseType),
      },
      {
        key: "location",
        label: "位置",
        render: (row: Row) => resolve.locationName((row as WarehouseRow).locationId),
      },
      {
        key: "status",
        label: "状态",
        render: (row: Row) => <StatusBadge active={(row as WarehouseRow).isActive} />,
      },
    ];
  }
  return [
    {
      key: "employeeNo",
      label: "员工编号",
      render: (row: Row) => (row as PersonnelRow).employeeNo ?? "—",
    },
    { key: "name", label: "姓名", render: (row: Row) => (row as PersonnelRow).name },
    {
      key: "company",
      label: "公司",
      render: (row: Row) => resolve.companyName((row as PersonnelRow).companyId),
    },
    {
      key: "department",
      label: "部门",
      render: (row: Row) => resolve.departmentName((row as PersonnelRow).departmentId),
    },
    {
      key: "costCenter",
      label: "成本中心",
      render: (row: Row) => resolve.departmentName((row as PersonnelRow).costCenterId),
    },
    {
      key: "manager",
      label: "直属上级",
      render: (row: Row) => resolve.personnelName((row as PersonnelRow).managerId),
    },
    {
      key: "contact",
      label: "联系方式",
      render: (row: Row) => (row as PersonnelRow).email ?? (row as PersonnelRow).phone ?? "—",
    },
    {
      key: "account",
      label: "绑定账号",
      render: (row: Row) =>
        (row as PersonnelRow).userId ? (
          <Badge variant="secondary">{resolve.userName((row as PersonnelRow).userId)}</Badge>
        ) : (
          <Badge variant="outline">未绑定</Badge>
        ),
    },
    {
      key: "status",
      label: "状态",
      render: (row: Row) => <StatusBadge active={(row as PersonnelRow).isActive} />,
    },
  ];
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function NativeSelect({
  value,
  onChange,
  children,
}: {
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      {children}
    </select>
  );
}

function renderFields(
  tab: TabKey,
  form: Record<string, FormValue>,
  setField: (key: string, value: FormValue) => void,
  context: {
    companies: CompanyRow[];
    departments: DepartmentRow[];
    users: { id: string; displayName: string }[];
    locations: LocationRow[];
    personnel: PersonnelRow[];
    editing: boolean;
  },
) {
  const nameField = (
    <Field key="name" label="名称" className={tab === "companies" ? "sm:col-span-2" : undefined}>
      <Input value={text(form.name)} onChange={(event) => setField("name", event.target.value)} />
    </Field>
  );
  const activeField = (
    <label key="active" className="flex items-center gap-2 pt-7 text-sm">
      <input
        type="checkbox"
        checked={bool(form.isActive)}
        onChange={(event) => setField("isActive", event.target.checked)}
        className="h-4 w-4 rounded border-border"
      />
      启用
    </label>
  );
  const companySelect = (
    <Field key="company" label="所属公司">
      <NativeSelect value={text(form.companyId)} onChange={(value) => setField("companyId", value)}>
        <option value="">未选择</option>
        {context.companies.map((company) => (
          <option key={company.id} value={company.id}>
            {company.name}
          </option>
        ))}
      </NativeSelect>
    </Field>
  );

  if (tab === "companies") {
    return [
      <Field key="code" label="编码（留空自动生成）">
        <Input
          value={text(form.code)}
          disabled={context.editing}
          onChange={(event) => setField("code", event.target.value)}
          placeholder="CO000001"
        />
      </Field>,
      nameField,
      activeField,
    ];
  }
  if (tab === "departments") {
    return [
      companySelect,
      <Field key="parent" label="上级部门">
        <NativeSelect value={text(form.parentId)} onChange={(value) => setField("parentId", value)}>
          <option value="">无</option>
          {context.departments
            .filter((department) => department.id !== text(form.id))
            .map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
        </NativeSelect>
      </Field>,
      nameField,
      <label key="cost" className="flex items-center gap-2 pt-7 text-sm">
        <input
          type="checkbox"
          checked={bool(form.isCostCenter)}
          onChange={(event) => setField("isCostCenter", event.target.checked)}
          className="h-4 w-4 rounded border-border"
        />
        标记为成本中心
      </label>,
      <Field key="manager" label="部门负责人">
        <NativeSelect
          value={text(form.managerUserId)}
          onChange={(value) => setField("managerUserId", value)}
        >
          <option value="">未选择</option>
          {context.users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.displayName}
            </option>
          ))}
        </NativeSelect>
      </Field>,
      <Field key="sort" label="排序">
        <Input
          type="number"
          value={text(form.sortOrder)}
          onChange={(event) => setField("sortOrder", event.target.value)}
        />
      </Field>,
      activeField,
    ];
  }
  if (tab === "locations") {
    return [
      companySelect,
      nameField,
      <Field key="address" label="详细地址" className="sm:col-span-2">
        <Input
          value={text(form.address)}
          onChange={(event) => setField("address", event.target.value)}
        />
      </Field>,
      <Field key="building" label="楼栋">
        <Input
          value={text(form.building)}
          onChange={(event) => setField("building", event.target.value)}
        />
      </Field>,
      <Field key="floor" label="楼层">
        <Input
          value={text(form.floor)}
          onChange={(event) => setField("floor", event.target.value)}
        />
      </Field>,
      <Field key="room" label="房间">
        <Input value={text(form.room)} onChange={(event) => setField("room", event.target.value)} />
      </Field>,
      activeField,
    ];
  }
  if (tab === "warehouses") {
    return [
      companySelect,
      nameField,
      <Field key="type" label="仓库类型">
        <NativeSelect
          value={text(form.warehouseType)}
          onChange={(value) => setField("warehouseType", value)}
        >
          <option value="entity">实体仓</option>
          <option value="virtual">虚拟仓</option>
          <option value="repair">维修仓</option>
          <option value="other">其他</option>
        </NativeSelect>
      </Field>,
      <Field key="location" label="位置">
        <NativeSelect
          value={text(form.locationId)}
          onChange={(value) => setField("locationId", value)}
        >
          <option value="">未选择</option>
          {context.locations.map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </NativeSelect>
      </Field>,
      <Field key="manager" label="负责人">
        <NativeSelect
          value={text(form.managerUserId)}
          onChange={(value) => setField("managerUserId", value)}
        >
          <option value="">未选择</option>
          {context.users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.displayName}
            </option>
          ))}
        </NativeSelect>
      </Field>,
      activeField,
    ];
  }
  return [
    <Field key="employeeNo" label="员工编号（留空自动生成）">
      <Input
        value={text(form.employeeNo)}
        disabled={context.editing}
        onChange={(event) => setField("employeeNo", event.target.value)}
        placeholder="PE000001"
      />
    </Field>,
    nameField,
    companySelect,
    <Field key="department" label="所属部门">
      <NativeSelect
        value={text(form.departmentId)}
        onChange={(value) => setField("departmentId", value)}
      >
        <option value="">未选择</option>
        {context.departments.map((department) => (
          <option key={department.id} value={department.id}>
            {department.name}
          </option>
        ))}
      </NativeSelect>
    </Field>,
    <Field key="manager" label="直属上级（人员关系）">
      <NativeSelect value={text(form.managerId)} onChange={(value) => setField("managerId", value)}>
        <option value="">未选择</option>
        {context.personnel
          .filter((person) => person.isActive)
          .map((person) => (
            <option key={person.id} value={person.id}>
              {person.name}
            </option>
          ))}
      </NativeSelect>
    </Field>,
    <Field key="position" label="岗位">
      <Input
        value={text(form.position)}
        onChange={(event) => setField("position", event.target.value)}
      />
    </Field>,
    <Field key="phone" label="手机号">
      <Input value={text(form.phone)} onChange={(event) => setField("phone", event.target.value)} />
    </Field>,
    <Field key="email" label="邮箱">
      <Input value={text(form.email)} onChange={(event) => setField("email", event.target.value)} />
    </Field>,
    <Field key="adid" label="ADID">
      <Input value={text(form.adid)} onChange={(event) => setField("adid", event.target.value)} />
    </Field>,
    activeField,
  ];
}
